import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';
import './EventSelector.css';

// Kalan süreyi hesapla
const calcRemaining = (targetDate) => {
  const diff = new Date(targetDate) - new Date();
  if (diff <= 0) return null;
  const days    = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours   = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, minutes, seconds, diff };
};

// Geri sayım hook'u
const useCountdown = (targetDate) => {
  const [remaining, setRemaining] = useState(() => targetDate ? calcRemaining(targetDate) : null);

  useEffect(() => {
    if (!targetDate) return;
    const interval = setInterval(() => {
      const r = calcRemaining(targetDate);
      setRemaining(r);
      if (!r) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  return remaining;
};

// Tek etkinlik için geri sayım bileşeni
const SaleCountdown = ({ saleStartDate }) => {
  const remaining = useCountdown(saleStartDate);

  if (!remaining) return null;

  const { days, hours, minutes, seconds } = remaining;

  return (
    <div className="sale-countdown">
      <div className="countdown-label">
        <i className="fa-solid fa-clock" /> Satışa Kalan Süre
      </div>
      <div className="countdown-timer">
        {days > 0 && (
          <div className="countdown-unit">
            <span className="countdown-num">{days}</span>
            <span className="countdown-text">gün</span>
          </div>
        )}
        <div className="countdown-unit">
          <span className="countdown-num">{String(hours).padStart(2, '0')}</span>
          <span className="countdown-text">saat</span>
        </div>
        <div className="countdown-unit">
          <span className="countdown-num">{String(minutes).padStart(2, '0')}</span>
          <span className="countdown-text">dk</span>
        </div>
        <div className="countdown-unit">
          <span className="countdown-num">{String(seconds).padStart(2, '0')}</span>
          <span className="countdown-text">sn</span>
        </div>
      </div>
    </div>
  );
};

const EventSelector = ({ onEventSelect, onCodeAccess, onBack }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Admin/EventOwner ise satış tarihi kontrolü bypass edilir
  const isPrivileged = user?.role === 'Admin' || user?.role === 'EventOwner';

  useEffect(() => { loadEvents(); }, []);

  const loadEvents = async () => {
    try {
      const data = await api.getEvents();
      setEvents(data.filter(e => e.isActive));
      setLoading(false);
    } catch (err) {
      console.error('Etkinlik yükleme hatası:', err);
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const isReservationType = (event) => parseInt(event.eventType) === 1;

  const getSaleStatus = useCallback((event) => {
    const now = new Date();
    // Etkinlik tarihi geçtiyse herkes için kilitli
    if (new Date(event.date) < now) return 'event_ended';
    // Admin/EventOwner için satış tarihi kontrolü atlanır
    if (isPrivileged) return 'open';
    if (event.saleStartDate && new Date(event.saleStartDate) > now) return 'not_started';
    if (event.saleEndDate   && new Date(event.saleEndDate)   < now) return 'closed';
    return 'open';
  }, [isPrivileged]);

  if (loading) return (
    <div className="loading">
      <i className="fa-solid fa-spinner fa-spin" /> Etkinlikler yükleniyor...
    </div>
  );

  return (
    <div className="event-selector">
      <div className="event-selector-header">
        <button onClick={onBack} className="back-btn">← Geri</button>
        <h2><i className="fa-solid fa-ticket" /> Bilet Al</h2>
      </div>

      <div className="events-grid">
        {events.length === 0 ? (
          <div className="no-events">Aktif etkinlik bulunamadı.</div>
        ) : (
          events.map(event => {
            const saleStatus = getSaleStatus(event);
            const saleClosed = saleStatus !== 'open';

            return (
              <div key={event.id} className="event-card">
                <div className="event-icon">
                  <i className={`fa-solid ${isReservationType(event) ? 'fa-clipboard-list' : 'fa-masks-theater'}`} />
                </div>

                <div className={`event-type-badge ${isReservationType(event) ? 'reservation' : 'ticket-sale'}`}>
                  {isReservationType(event)
                    ? <><i className="fa-solid fa-clipboard-list" /> Rezervasyon</>
                    : <><i className="fa-solid fa-ticket" /> Bilet Satışı</>}
                </div>

                <h3>{event.name}</h3>
                <div className="event-date">
                  <i className="fa-solid fa-calendar" /> {formatDate(event.date)}
                </div>
                {event.description && <p className="event-description">{event.description}</p>}
                <div className="event-pricing">
                  <span>Öğrenci Bileti: {event.enableStudentDiscount
                    ? <i className="fa-solid fa-circle-check" style={{color:'#22c55e'}} />
                    : <i className="fa-solid fa-circle-xmark" style={{color:'#ef4444'}} />}
                  </span>
                </div>

                {/* Satış durumu + geri sayım */}
                {saleStatus === 'event_ended' && (
                  <div className="sale-status closed">
                    <i className="fa-solid fa-flag-checkered" /> Etkinlik sona erdi
                  </div>
                )}
                {saleStatus === 'not_started' && (
                  <>
                    <div className="sale-status not-started">
                      <i className="fa-solid fa-lock" /> Satışlar{' '}
                      {new Date(event.saleStartDate).toLocaleDateString('tr-TR', {
                        day: 'numeric', month: 'long', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })} tarihinde başlayacak
                    </div>
                    <SaleCountdown saleStartDate={event.saleStartDate} />
                  </>
                )}
                {saleStatus === 'closed' && (
                  <div className="sale-status closed">
                    <i className="fa-solid fa-lock" /> Satışlar Kapandı
                  </div>
                )}

                {/* Admin badge */}
                {isPrivileged && event.saleStartDate && new Date(event.saleStartDate) > new Date() && (
                  <div className="admin-bypass-note">
                    <i className="fa-solid fa-shield-halved" /> Yönetici erişimi — satış tarihi kontrolü atlandı
                  </div>
                )}

                <div className="event-actions">
                  {isReservationType(event) ? (
                    <button
                      onClick={() => !saleClosed && onCodeAccess?.(event)}
                      className={`select-event-btn code-required-btn ${saleClosed ? 'disabled-btn' : ''}`}
                      disabled={saleClosed}
                    >
                      <i className="fa-solid fa-key" /> Erişim Kodu ile Rezervasyon Yap
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => !saleClosed && onEventSelect(event)}
                        className={`select-event-btn ${saleClosed ? 'disabled-btn' : ''}`}
                        disabled={saleClosed}
                      >
                        <i className="fa-solid fa-ticket" /> Bilet Al
                      </button>
                      {!saleClosed && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onCodeAccess?.(event); }}
                          className="code-access-btn"
                          title="Davetiye kodum var"
                        >
                          <i className="fa-solid fa-key" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EventSelector;